import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import {
  Store as StoreIcon,
  AttachMoney as MoneyIcon,
  EventNote as ReservationIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { collection, query, where, getDocs, getCountFromServer, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Shop } from '../types';

interface Stats {
  totalShops: number;
  paidShops: number;
  totalReservations: number;
  activeUsers: number;
}

interface PlanStats {
  name: string;
  value: number;
  color: string;
  [key: string]: string | number;
}

interface MonthlyStats {
  month: string;
  shops: number;
  users: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<Stats>({
    totalShops: 0,
    paidShops: 0,
    totalReservations: 0,
    activeUsers: 0
  });
  const [loading, setLoading] = useState(true);
  const [recentShops, setRecentShops] = useState<Shop[]>([]);
  const [planStats, setPlanStats] = useState<PlanStats[]>([]);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats[]>([]);

  useEffect(() => {
    fetchStats();
    fetchRecentShops();
    fetchPlanStats();
    fetchMonthlyStats();
  }, []);

  const fetchStats = async () => {
    try {
      // 店舗数
      const shopsSnapshot = await getCountFromServer(collection(db, 'shops'));
      const totalShops = shopsSnapshot.data().count;

      // 有料プラン店舗数
      const paidShopsQuery = query(
        collection(db, 'shops'),
        where('subscription.planId', 'in', ['basic', 'premium'])
      );
      const paidShopsSnapshot = await getCountFromServer(paidShopsQuery);
      const paidShops = paidShopsSnapshot.data().count;

      // 予約数（仮）
      const reservationsSnapshot = await getCountFromServer(collection(db, 'reservations'));
      const totalReservations = reservationsSnapshot.data().count;

      // ユーザー数
      const usersSnapshot = await getCountFromServer(collection(db, 'users'));
      const activeUsers = usersSnapshot.data().count;

      setStats({
        totalShops,
        paidShops,
        totalReservations,
        activeUsers
      });
    } catch (error) {
      console.error('統計データの取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentShops = async () => {
    try {
      const q = query(
        collection(db, 'shops'),
        orderBy('createdAt', 'desc'),
        limit(5)
      );
      const snapshot = await getDocs(q);
      const shops = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
          updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
        };
      }) as Shop[];
      setRecentShops(shops);
    } catch (error) {
      console.error('最近の店舗取得エラー:', error);
    }
  };

  const fetchPlanStats = async () => {
    try {
      const shopsSnapshot = await getDocs(collection(db, 'shops'));
      const planCounts: Record<string, number> = {};

      shopsSnapshot.docs.forEach(doc => {
        const planId = doc.data().subscription?.planId || 'free';
        planCounts[planId] = (planCounts[planId] || 0) + 1;
      });

      const planColors: Record<string, string> = {
        free: '#9E9E9E',
        basic: '#2196F3',
        premium: '#FF9800',
      };

      const planNames: Record<string, string> = {
        free: '無料プラン',
        basic: 'ベーシックプラン',
        premium: 'プレミアムプラン',
      };

      const stats: PlanStats[] = Object.entries(planCounts).map(([plan, count]) => ({
        name: planNames[plan] || plan,
        value: count,
        color: planColors[plan] || '#000000',
      }));

      setPlanStats(stats);
    } catch (error) {
      console.error('プラン統計取得エラー:', error);
    }
  };

  const fetchMonthlyStats = async () => {
    try {
      // 過去6ヶ月の統計を実データから取得
      const now = new Date();
      const monthlyData: MonthlyStats[] = [];

      for (let i = 5; i >= 0; i--) {
        const targetDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const nextMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
        const monthName = `${targetDate.getMonth() + 1}月`;

        // その月に登録された店舗数を取得
        const shopsQuery = query(
          collection(db, 'shops'),
          where('createdAt', '>=', Timestamp.fromDate(targetDate)),
          where('createdAt', '<', Timestamp.fromDate(nextMonth))
        );
        const shopsSnapshot = await getDocs(shopsQuery);
        const shopsCount = shopsSnapshot.size;

        // その月に登録されたユーザー数を取得
        const usersQuery = query(
          collection(db, 'users'),
          where('createdAt', '>=', Timestamp.fromDate(targetDate)),
          where('createdAt', '<', Timestamp.fromDate(nextMonth))
        );
        const usersSnapshot = await getDocs(usersQuery);
        const usersCount = usersSnapshot.size;

        monthlyData.push({
          month: monthName,
          shops: shopsCount,
          users: usersCount,
        });
      }

      setMonthlyStats(monthlyData);
    } catch (error) {
      console.error('月別統計取得エラー:', error);
      // エラー時は空配列を設定
      setMonthlyStats([]);
    }
  };

  const statCards = [
    {
      title: '登録店舗数',
      value: stats.totalShops,
      icon: <StoreIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
      color: 'primary.light'
    },
    {
      title: '有料プラン契約数',
      value: stats.paidShops,
      icon: <MoneyIcon sx={{ fontSize: 40, color: 'success.main' }} />,
      color: 'success.light'
    },
    {
      title: '総予約数',
      value: stats.totalReservations,
      icon: <ReservationIcon sx={{ fontSize: 40, color: 'info.main' }} />,
      color: 'info.light'
    },
    {
      title: 'アクティブユーザー',
      value: stats.activeUsers,
      icon: <PeopleIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
      color: 'warning.light'
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        ダッシュボード
      </Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 3, mt: 2 }}>
        {statCards.map((card, index) => (
          <Card key={index}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography color="text.secondary" gutterBottom variant="body2">
                    {card.title}
                  </Typography>
                  <Typography variant="h4">
                    {loading ? '-' : card.value.toLocaleString()}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    bgcolor: card.color,
                    borderRadius: 2,
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {card.icon}
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* 月別成長チャート */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          月別成長トレンド
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={monthlyStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="shops" fill="#2196F3" name="新規店舗" />
            <Bar dataKey="users" fill="#FF9800" name="新規ユーザー" />
          </BarChart>
        </ResponsiveContainer>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3, mt: 3 }}>
        {/* 最近の登録店舗 */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            最近の登録店舗
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>店舗名</TableCell>
                  <TableCell>プラン</TableCell>
                  <TableCell>ステータス</TableCell>
                  <TableCell>登録日</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentShops.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      データがありません
                    </TableCell>
                  </TableRow>
                ) : (
                  recentShops.map((shop) => (
                    <TableRow key={shop.id} hover>
                      <TableCell>{shop.shopName || shop.name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={shop.subscription?.planId || 'free'}
                          size="small"
                          color={shop.subscription?.planId === 'free' ? 'default' : 'primary'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={shop.subscription?.status === 'active' ? '有効' : '無効'}
                          size="small"
                          color={shop.subscription?.status === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {shop.createdAt instanceof Date
                          ? shop.createdAt.toLocaleDateString('ja-JP')
                          : shop.createdAt?.toDate?.()?.toLocaleDateString('ja-JP') || '-'
                        }
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* プラン別割合 */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            プラン別割合
          </Typography>
          {planStats.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              データがありません
            </Typography>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={planStats}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${percent ? (percent * 100).toFixed(0) : 0}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {planStats.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Dashboard;
